import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { Crown, Check, Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import DashboardLayout from '../components/DashboardLayout';
import { FREE_DAILY_SEARCHES } from '../config/quotas';
import { PREMIUM_PRICE_LABEL } from '../config/pricing';

function Pricing() {
  const { currentUser, isTrial, isTrialExpired, trialDaysLeft } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    const handleManageSubscription = async () => {
      setLoading(true);
      try {
        const token = await auth.currentUser.getIdToken();

        const response = await fetch(
          'https://europe-west1-medaviceia.cloudfunctions.net/createPortalSession',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        window.location.href = data.url;
      } catch (error) {
        console.error('Erreur:', error);
        alert("Erreur lors de l'ouverture du portail");
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();

      const response = await fetch(
        'https://europe-west1-medaviceia.cloudfunctions.net/createCheckoutSession',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Response error:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Erreur complète:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();

      const response = await fetch(
        'https://europe-west1-medaviceia.cloudfunctions.net/createPortalSession',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Erreur:', error);
      alert("Erreur lors de l'ouverture du portail");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="mb-8 flex items-center gap-2 dark:border-gray-700 dark:text-gray-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au Dashboard
        </Button>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-6 shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Choisissez votre plan
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Accédez à des conseils médicaux illimités et personnalisés
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Plan Gratuit */}
          <Card className="relative p-8 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-2xl hover:shadow-xl transition-all">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Gratuit</h2>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-gray-900 dark:text-gray-100">0€</span>
                <span className="text-gray-500 dark:text-gray-400">/mois</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300">{FREE_DAILY_SEARCHES} recherches par jour</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300">Accès chat médical</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300">Recherche médicaments</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300">Accès forum communautaire</span>
              </li>
            </ul>

            <Button
              disabled
              className="w-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            >
              {/* Pendant l'essai, le plan gratuit n'est PAS le plan actuel. */}
              {currentUser?.subscription_type === 'free' && !isTrial
                ? 'Plan actuel'
                : 'Plan gratuit'}
            </Button>
          </Card>

          {/* Plan Premium */}
          <Card className="relative p-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-2 border-amber-500 dark:border-amber-600 rounded-2xl hover:shadow-2xl transition-all">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Recommandé
              </div>
            </div>

            <div className="mb-6 mt-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                <Crown className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                Premium
              </h2>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                  {PREMIUM_PRICE_LABEL}
                </span>
                <span className="text-gray-600 dark:text-gray-400">/mois</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Recherches illimitées
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Accès prioritaire
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Accès à toutes les fonctionnalités
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Réponses plus détaillées
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Historique complet
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  Support prioritaire
                </span>
              </li>
            </ul>

            {currentUser?.subscription_type === 'premium' ? (
              <div className="space-y-3">
                <Button
                  disabled
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Abonnement actif
                </Button>
                <Button
                  onClick={handleManageSubscription}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-2 border-amber-500 dark:border-amber-600 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Chargement...
                    </>
                  ) : (
                    'Gérer mon abonnement'
                  )}
                </Button>
              </div>
            ) : (
              <>
                {(isTrial || isTrialExpired) && (
                  <p className="mb-3 text-center text-sm font-medium text-amber-700 dark:text-amber-400">
                    {/* Page de conversion : on garde une phrase, pas un code « J-N ».
                        Au-delà du dernier jour trialDaysLeft >= 2, donc pluriel constant. */}
                    {isTrial
                      ? trialDaysLeft === 1
                        ? 'Essai actif — dernier jour'
                        : `Essai actif — il vous reste ${trialDaysLeft} jours`
                      : 'Votre essai de 7 jours est terminé'}
                  </p>
                )}
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    S'abonner maintenant
                  </>
                )}
              </Button>
              </>
            )}
          </Card>
        </div>

        <div className="mt-16 max-w-3xl mx-auto">
          <Card className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-2 border-blue-200 dark:border-blue-800">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              💡 Informations importantes
            </h3>
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Annulation possible à tout moment</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Paiement 100% sécurisé par Stripe</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Accès immédiat après paiement</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Données médicales confidentielles (conformité RGPD)</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Pricing;
